import {
  hasNoProcedures,
  isProcedureMissingDescription,
} from "pages/CreateOrder/utils/createEstimateBlockers";
import { UNLINKED_PAYOUT_PROVIDERS_FIELD } from "pages/CreateOrder/utils/payoutProviderSetup";
import { useMemo } from "react";
import { useFormContext, useFormState } from "react-hook-form";

import { useEligibilityActivity } from "./eligibilityActivity";

/**
 * Whether the estimate can be created or saved right now — the same condition
 * that disables the footer's actions, so the footer's hint text and the buttons
 * can never disagree.
 *
 * CLE-2971 — never while an eligibility check is pending (see `eligibilityActivity.ts`): the
 * estimate would be created on the previous Service Type's benefits while the re-run's answer is
 * stored but never attached to the order.
 *
 * Save Draft passes `allowPendingEligibility` — it is not gated by a pending check (PR #1364), only by
 * the other terms. Create and Schedule (and the footer hint) use the default.
 *
 * CLE-3074 — never while a selected provider's payment setup is incomplete on a Finix account
 * (UNLINKED_PAYOUT_PROVIDERS_FIELD, written by the service-details section): the API would refuse it.
 * Save Draft passes `allowUnlinkedPayoutProviders` — a draft is checked again when it is approved.
 */
export function useOrderReadiness({
  allowPendingEligibility = false,
  allowUnlinkedPayoutProviders = false,
}: {
  allowPendingEligibility?: boolean;
  allowUnlinkedPayoutProviders?: boolean;
} = {}): boolean {
  const { getValues, watch } = useFormContext();
  const { isValid } = useFormState();
  const procedures = watch("serviceDetails");
  const isEditingMessage = watch("isEditingMessage");
  const unlinkedPayoutProviders = watch(UNLINKED_PAYOUT_PROVIDERS_FIELD);
  const isCheckingEligibility = useEligibilityActivity();

  return useMemo(() => {
    const hasEmptyCptDescription = getValues()?.serviceDetails?.some(
      isProcedureMissingDescription
    );

    return (
      isValid &&
      !hasNoProcedures(procedures) &&
      !hasEmptyCptDescription &&
      !isEditingMessage &&
      (allowPendingEligibility || !isCheckingEligibility) &&
      (allowUnlinkedPayoutProviders || !unlinkedPayoutProviders?.length)
    );
  }, [
    getValues,
    isValid,
    procedures,
    isEditingMessage,
    isCheckingEligibility,
    allowPendingEligibility,
    unlinkedPayoutProviders,
    allowUnlinkedPayoutProviders,
  ]);
}
